import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {Moment} from 'moment';
import {User} from '../../../../backend/interfaces/user';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {TFunction} from 'i18next';
import {translations} from '../../../../i18n/translations';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {renderCanvasToPdf} from '../base/export-pdf-render-canvas';
import {formatPdfDateRangeTitle, pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {wait} from '../../../functions/wait';
import type {PreparedPdfExportFile} from '../base/export-data-types';
import {buildExportFileName} from '../base/export-file-name';

const PDF_MARGIN = 14;
const PDF_EXPORTING_CLASS = 'pdf-exporting';

type CareServicesPDFParams = {
  startDate: Moment;
  endDate: Moment;
  user?: User;
  entity?: EntityInfo;
  t: TFunction;
};

type CareServicesElementPDFParams = {
  startDate: Moment;
  endDate: Moment;
  user?: User;
  entity?: EntityInfo;
  t: TFunction;
  elementClassName: string;
  /** Custom width for the PDF content */
  targetWidth?: number;
  /** Whether to allow splitting table rows across PDF pages if necessary */
  allowTableRowPagination?: boolean;
};

/**
 * Converts the FullCalendar DOM element into a PDF document.
 * Uses html2canvas to capture the visual calendar and jsPDF to create the PDF.
 * The calendar is rendered with fixed dimensions to ensure consistent PDF output
 * regardless of the current viewport size.
 */
export async function prepareCareServicesCalendarPDF({
  startDate,
  endDate,
  user,
  entity,
  t,
}: CareServicesPDFParams): Promise<PreparedPdfExportFile> {
  return prepareCareServicesElementPDF({
    startDate,
    endDate,
    user,
    entity,
    t,
    elementClassName: 'care-services-calendar',
    allowTableRowPagination: false, // one page rendering, always.
  });
}

export async function prepareCareServicesTablePDF({
  startDate,
  endDate,
  user,
  entity,
  t,
}: CareServicesPDFParams): Promise<PreparedPdfExportFile> {
  return prepareCareServicesElementPDF({
    startDate,
    endDate,
    user,
    entity,
    t,
    elementClassName: 'care-services-table',
  });
}

async function prepareCareServicesElementPDF({
  startDate,
  endDate,
  user,
  entity,
  t,
  elementClassName,
  targetWidth,
  allowTableRowPagination = true,
}: CareServicesElementPDFParams): Promise<PreparedPdfExportFile> {
  try {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const reportTitle = user
      ? t(translations.seniorSideMenu.careServices)
      : t(translations.teamSideMenu.careServices);
    const reportDateRange = formatPdfDateRangeTitle({startDate, endDate});

    pdf.setProperties({title: `${reportTitle}: ${reportDateRange}`});

    const cursorY = pdfAddBodyTitle(pdf, {
      title: reportTitle,
      subtitle: reportDateRange,
    });

    const pageCount = await printElementContent(
      pdf,
      elementClassName,
      targetWidth,
      allowTableRowPagination,
      cursorY
    );

    pdf.setPage(1);
    pdfHeaderInfo(pdf, user, entity, t);

    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      pdf.setPage(pageIndex);
      pdfFooterInfo(pdf, `${pageIndex}`);
    }

    const fileName = buildExportFileName([
      user?.displayName ?? user?.name ?? entity?.shortName ?? entity?.name,
      user
        ? t(translations.seniorSideMenu.careServices)
        : t(translations.teamSideMenu.careServices),
    ]);

    return {
      file: pdf,
      fileName,
    };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF from care services view');
  }
}

async function printElementContent(
  pdf: jsPDF,
  elementClassName: string,
  targetWidth: number | undefined,
  allowTableRowPagination: boolean,
  startPosY: number
): Promise<number> {
  const contentElement = document.getElementsByClassName(elementClassName)[0] as HTMLElement;

  if (!contentElement) {
    throw new Error('Care services export element not found');
  }

  // Store original styles to restore later
  const originalWidth = contentElement.style.width;
  const originalHeight = contentElement.style.height;
  const originalOverflow = contentElement.style.overflow;
  const originalDisplay = contentElement.style.display;
  const originalMaxHeight = contentElement.style.maxHeight;
  const originalMaxWidth = contentElement.style.maxWidth;
  const originalZIndex = contentElement.style.zIndex;
  const hadExportingClass = contentElement.classList.contains(PDF_EXPORTING_CLASS);

  // Prepare element styles for full capture
  contentElement.style.overflow = 'visible';
  contentElement.style.display = 'block';
  contentElement.style.maxHeight = 'none';
  contentElement.style.maxWidth = 'none';
  contentElement.style.zIndex = '-9999';
  if (!hadExportingClass) {
    contentElement.classList.add(PDF_EXPORTING_CLASS);
  }

  // Force layout after export-specific CSS is applied so measurements are up to date.
  void contentElement.offsetWidth;

  const fullScrollWidth = Math.ceil(contentElement.scrollWidth);
  const fullScrollHeight = Math.ceil(contentElement.scrollHeight);
  let captureWidth = targetWidth ?? fullScrollWidth;
  const firstPageContentHeight = pdf.internal.pageSize.getHeight() - startPosY - 14;

  if (!allowTableRowPagination && firstPageContentHeight > 0) {
    const minimumWidthForFullPage = Math.ceil(
      (fullScrollHeight * (pdf.internal.pageSize.getWidth() - PDF_MARGIN * 2)) /
        firstPageContentHeight
    );

    captureWidth = Math.max(captureWidth, minimumWidthForFullPage);
  }

  contentElement.style.width = `${captureWidth}px`;
  const captureHeight = Math.ceil(contentElement.scrollHeight);
  contentElement.style.height = `${captureHeight}px`;

  await wait(250);

  try {
    // Capture the element as a canvas
    const canvas = await html2canvas(contentElement, {
      scale: 3, // Higher quality
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      windowHeight: captureHeight,
      logging: false,
    });

    return renderCanvasToPdf({
      pdf,
      canvas,
      tableContentElement: allowTableRowPagination ? contentElement : undefined,
      capturedHeight: captureHeight,
      topMargin: startPosY,
      leftMargin: PDF_MARGIN,
      rightMargin: PDF_MARGIN,
      bottomMargin: PDF_MARGIN,
    });
  } finally {
    // Restore original styles
    contentElement.style.width = originalWidth;
    contentElement.style.height = originalHeight;
    contentElement.style.overflow = originalOverflow;
    contentElement.style.display = originalDisplay;
    contentElement.style.maxHeight = originalMaxHeight;
    contentElement.style.maxWidth = originalMaxWidth;
    contentElement.style.zIndex = originalZIndex;
    if (!hadExportingClass) {
      contentElement.classList.remove(PDF_EXPORTING_CLASS);
    }
  }
}
